import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Logo } from './Logo';
import { filterNavForUser, navGroupLabels, navItems } from '@/config/navigation';
import { resolveWorkspaceNav } from '@zafroi/ui-kit';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { cn } from '@/lib/utils';

export function MobileNav() {
  const [open, setOpen] = useState(false);
  const me = useCurrentUser();
  // Unified workspace nav (ADR-041/051) — when present, the mobile menu mirrors
  // the desktop sidebar: the one cross-app nav rather than the local Ops nav.
  const unifiedNav = me?.workspace?.nav ?? null;
  const unifiedGroups =
    unifiedNav && unifiedNav.length
      ? resolveWorkspaceNav(unifiedNav, 'operations', me?.workspace?.apps)
      : null;

  const visibleItems = filterNavForUser(navItems, me);
  const grouped = visibleItems.reduce<Record<string, typeof visibleItems>>((acc, item) => {
    (acc[item.group] ??= []).push(item);
    return acc;
  }, {});

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="md:hidden" aria-label="Open menu">
          <Menu className="h-5 w-5" />
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-[280px] p-0">
        <SheetHeader className="border-b">
          <SheetTitle>
            <Logo variant="lockup" taglineClassName="text-muted-foreground" />
          </SheetTitle>
        </SheetHeader>
        <nav className="px-3 py-4">
          {unifiedGroups
            ? unifiedGroups.map((g) => (
                <div key={g.id} className="mb-5">
                  {g.label && (
                    <div className="px-2 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                      {g.label}
                    </div>
                  )}
                  <ul className="space-y-0.5">
                    {g.items.map((item) => {
                      const Icon = item.icon;
                      return (
                        <li key={item.id}>
                          <a
                            href={item.href}
                            onClick={() => setOpen(false)}
                            className="flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-foreground/80 transition-colors hover:bg-muted"
                          >
                            <Icon className="h-[18px] w-[18px] shrink-0" />
                            <span>{item.label}</span>
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))
            : Object.entries(grouped).map(([group, items]) => {
            const label = navGroupLabels[group as keyof typeof navGroupLabels];
            return (
            <div key={group} className="mb-5">
              {label && (
                <div className="px-2 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  {label}
                </div>
              )}
              <ul className="space-y-0.5">
                {items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        end={item.to === '/'}
                        onClick={() => setOpen(false)}
                        className={({ isActive }) =>
                          cn(
                            'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                            isActive
                              ? 'bg-muted text-foreground'
                              : 'text-foreground/80 hover:bg-muted',
                          )
                        }
                      >
                        <Icon className="h-[18px] w-[18px] shrink-0" />
                        <span>{item.label}</span>
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </div>
            );
          })}
        </nav>
      </SheetContent>
    </Sheet>
  );
}
